<template>
  <div class="au-formgroup">
    <!-- Invite-required gate -->
    <template v-if="!props.inviteToken">
      <div class="au-card au-card--center">
        <span class="au-gate-mark">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <rect x="5" y="11" width="14" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
        </span>
        <p class="au-gate-title">{{ STRINGS.auth.closedBeta.inviteRequired }}</p>
        <p class="au-gate-body">{{ STRINGS.auth.closedBeta.inviteExplanation }}</p>
        <button
          class="au-link au-link--strong"
          data-testid="invite-required-login-link"
          @click="emit('navigate', 'login')"
        >
          {{ STRINGS.auth.links.logIn }}
        </button>
      </div>

      <div class="au-flinks">
        <button
          class="au-link au-link--muted"
          data-testid="signup-back-link"
          @click="emit('navigate', 'homepage')"
        >
          {{ STRINGS.auth.links.backToHome }}
        </button>
      </div>
    </template>

    <!-- Invited signup form -->
    <template v-else>
      <form
        class="au-card"
        :aria-label="STRINGS.aria.labels.signupForm"
        @submit.prevent="handleSubmit"
        autocomplete="on"
        novalidate
      >
        <!-- Display Name -->
        <div class="au-field">
          <label for="signup-name" class="au-label">{{ STRINGS.auth.labels.displayName }}</label>
          <div class="au-input-wrap">
            <InputText
              id="signup-name"
              ref="nameInput"
              v-model="displayName"
              type="text"
              name="username"
              class="au-input"
              :placeholder="STRINGS.auth.placeholders.displayName"
              autocomplete="off"
              :maxlength="UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH"
              autocorrect="off"
              spellcheck="false"
              data-testid="signup-name-input"
              :aria-invalid="!!nameError"
              @blur="nameTouched = true"
              @keyup.enter="handleSubmit"
            />
          </div>
          <p
            v-if="nameError"
            class="au-hint au-hint--err"
            role="alert"
            aria-live="polite"
            data-testid="signup-name-error"
          >
            {{ nameError }}
          </p>
          <p v-else class="au-hint">{{ STRINGS.auth.hints.displayName }}</p>
        </div>

        <!-- Email -->
        <div class="au-field">
          <label for="signup-email" class="au-label">{{ STRINGS.auth.labels.email }}</label>
          <div class="au-input-wrap">
            <InputText
              id="signup-email"
              v-model="email"
              type="email"
              name="email"
              class="au-input"
              :placeholder="STRINGS.auth.placeholders.email"
              autocomplete="username"
              data-testid="signup-email-input"
              @keyup.enter="handleSubmit"
            />
          </div>
          <p class="au-hint">{{ STRINGS.auth.hints.email }}</p>
        </div>

        <!-- Password -->
        <div class="au-field">
          <label for="signup-password" class="au-label">{{ STRINGS.auth.labels.password }}</label>
          <div class="au-input-wrap">
            <InputText
              id="signup-password"
              v-model="password"
              name="new-password"
              :type="showPassword ? 'text' : 'password'"
              class="au-input au-input--pw"
              :placeholder="STRINGS.auth.placeholders.password"
              autocomplete="new-password"
              data-testid="signup-password-input"
              @keyup.enter="handleSubmit"
            />
            <PasswordToggle :pressed="showPassword" @toggle="showPassword = !showPassword" />
          </div>
          <p class="au-hint">{{ STRINGS.auth.hints.password }}</p>
        </div>

        <!-- Error display -->
        <p
          v-if="displayError"
          ref="errorRef"
          class="au-hint au-hint--err text-center"
          role="alert"
          aria-live="polite"
          tabindex="-1"
        >
          {{ displayError }}
        </p>

        <!-- Legal agreement -->
        <p class="au-legal-agree" data-testid="signup-legal-agreement">
          {{ STRINGS.legal.signup.agreement }}
          <button class="au-legal-link" @click="emit('navigate', 'terms')" type="button">
            {{ STRINGS.legal.signup.termsLink }}
          </button>
          {{ STRINGS.legal.signup.and }}
          <button class="au-legal-link" @click="emit('navigate', 'privacy')" type="button">
            {{ STRINGS.legal.signup.privacyLink }}</button
          >.
        </p>

        <!-- Submit button -->
        <button
          type="button"
          class="au-btn au-btn--primary au-mt"
          :disabled="!canSubmit"
          :aria-label="STRINGS.aria.labels.createAccount"
          data-testid="signup-submit-button"
          @click="handleSubmit"
        >
          <template v-if="auth.isLoading.value">
            {{ STRINGS.auth.buttons.creatingAccount }}<LoadingDots />
          </template>
          <template v-else>
            {{ STRINGS.auth.buttons.createAccount }}
          </template>
        </button>
      </form>

      <!-- Footer links -->
      <div class="au-flinks">
        <p class="au-flinks-row">
          {{ STRINGS.auth.links.haveAccount }}
          <button
            class="au-link au-link--strong"
            @click="emit('navigate', 'login')"
            data-testid="signup-to-login-link"
          >
            {{ STRINGS.auth.links.logIn }}
          </button>
        </p>
        <button
          class="au-link au-link--muted"
          @click="emit('navigate', 'homepage')"
          data-testid="signup-back-link"
        >
          {{ STRINGS.auth.links.backToHome }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import PasswordToggle from './ui/PasswordToggle.vue';
import LoadingDots from './ui/LoadingDots.vue';
import { useAuth } from '@/composables/useAuth';
import { isAuthError } from '@/types/auth';
import { AUTH_CONFIG, UI_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const props = defineProps<{ inviteToken?: string }>();

const emit = defineEmits<{
  success: [name: string];
  navigate: [target: 'login' | 'homepage' | 'terms' | 'privacy'];
}>();

const auth = useAuth();

const displayName = ref('');
const email = ref('');
const password = ref('');
const showPassword = ref(false);
const localError = ref('');
const nameTouched = ref(false);
const nameInput = ref();
const errorRef = ref<HTMLElement>();

const nameError = computed(() => {
  if (!nameTouched.value) return '';
  const trimmed = displayName.value.trim();
  if (!trimmed) return '';
  if (!UI_CONFIG.VALIDATION.DISPLAY_NAME_PATTERN.test(trimmed)) {
    return STRINGS.auth.errors.nameInvalidChars;
  }
  if (trimmed.length < UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH) {
    return STRINGS.auth.errors.nameTooShort;
  }
  return '';
});

const displayError = computed(() => {
  if (auth.rateLimitCountdown.value > 0) {
    return STRINGS.templates.rateLimitCountdown(auth.rateLimitCountdown.value);
  }
  if (auth.error.value) {
    return auth.error.value.message;
  }
  return localError.value;
});

const canSubmit = computed(() => {
  const trimmed = displayName.value.trim();
  return (
    trimmed.length >= UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH &&
    trimmed.length <= UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH &&
    UI_CONFIG.VALIDATION.DISPLAY_NAME_PATTERN.test(trimmed) &&
    password.value.length > 0 &&
    !auth.isLoading.value &&
    auth.rateLimitCountdown.value === 0
  );
});

async function handleSubmit(): Promise<void> {
  if (!props.inviteToken) return;
  if (!canSubmit.value) return;

  localError.value = '';
  auth.clearError();

  const trimmedName = displayName.value.trim();

  if (password.value.length < AUTH_CONFIG.PASSWORD_MIN_LENGTH) {
    localError.value = STRINGS.auth.errors.passwordTooShort;
    focusError();
    return;
  }

  const trimmedEmail = email.value.trim();
  if (!trimmedEmail) {
    localError.value = STRINGS.auth.errors.emailRequired;
    focusError();
    return;
  }
  if (!isValidEmail(trimmedEmail)) {
    localError.value = STRINGS.auth.errors.emailInvalid;
    focusError();
    return;
  }

  try {
    await auth.signup({
      name: trimmedName,
      email: trimmedEmail,
      password: password.value,
      invite_token: props.inviteToken,
    });
    emit('success', trimmedName);
  } catch (err: unknown) {
    if (!isAuthError(err)) {
      reportClientError(err, ErrorLocation.AuthSignupUnexpected);
    }
    focusError();
  }
}

function isValidEmail(value: string): boolean {
  const idx = value.indexOf('@');
  return idx > 0 && idx < value.length - 1;
}

function focusError(): void {
  nextTick(() => {
    errorRef.value?.focus();
  });
}

onMounted(() => {
  nextTick(() => {
    const el = nameInput.value?.$el ?? nameInput.value;
    const input = el?.querySelector?.('input') ?? el;
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  });
});
</script>
